import {useAtom} from 'jotai';
import {atomWithStorage} from 'jotai/utils';
import {useCallback, useMemo} from 'react';

import {TileId} from './dashboardModel';
import {LIVE_TILE_ORDER, TileContentStatus} from './liveDashboard';

export interface TilePresenceEntry {
  lastPresentAt: number;
  missStreak: number;
}

/** Per-user remembered tiles for stable live dashboard layout across visits. */
export type TilePresenceStore = Record<string, Partial<Record<TileId, TilePresenceEntry>>>;

const MISS_STREAK_FORGET = 3;
const PRESENCE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export const tilePresenceAtom = atomWithStorage<TilePresenceStore>('dashboard:tilePresence', {});

/** Whether a remembered tile should stay in the presence set (exported for tests). */
export const shouldRememberTilePresence = (entry: TilePresenceEntry, now: number): boolean => {
  if (entry.missStreak >= MISS_STREAK_FORGET) {
    return false;
  }
  if (now - entry.lastPresentAt > PRESENCE_MAX_AGE_MS) {
    return false;
  }
  return true;
};

export const useTilePresence = (userId: string | undefined) => {
  const [store, setStore] = useAtom(tilePresenceAtom);

  const userEntries = useMemo(() => (userId ? (store[userId] ?? {}) : {}), [store, userId]);

  const remembered = useMemo(() => {
    const now = Date.now();
    const set = new Set<TileId>();
    (Object.entries(userEntries) as [TileId, TilePresenceEntry][]).forEach(([tileId, entry]) => {
      if (shouldRememberTilePresence(entry, now)) {
        set.add(tileId);
      }
    });
    return set;
  }, [userEntries]);

  const isFirstEverLoad = !userId || Object.keys(userEntries).length === 0;

  const recordStatuses = useCallback(
    (statuses: Partial<Record<TileId, TileContentStatus>>) => {
      if (!userId) {
        return;
      }
      setStore((prev) => {
        const userPrev = prev[userId] ?? {};
        const nextUser: Partial<Record<TileId, TilePresenceEntry>> = {...userPrev};
        const now = Date.now();

        LIVE_TILE_ORDER.forEach((tileId) => {
          const status = statuses[tileId];
          if (!status) {
            return;
          }
          const prevEntry = nextUser[tileId];
          if (status === 'present') {
            nextUser[tileId] = {lastPresentAt: now, missStreak: 0};
            return;
          }
          if (status === 'empty' && prevEntry) {
            const updated: TilePresenceEntry = {
              ...prevEntry,
              missStreak: prevEntry.missStreak + 1,
            };
            if (shouldRememberTilePresence(updated, now)) {
              nextUser[tileId] = updated;
            } else {
              delete nextUser[tileId];
            }
          }
        });

        return {...prev, [userId]: nextUser};
      });
    },
    [userId, setStore]
  );

  return {remembered, isFirstEverLoad, recordStatuses};
};
